import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Space, Spin } from 'antd'

import { UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  MetadataTree,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { renderChallengeEvent } from '../../helpers'
import {
  useChallengesQuery,
  useDeleteChallengeMutation,
  useSaveChallengesMutation,
} from '../../hooks'
import {
  addChallenge,
  renameChallenge,
  resetChallengeTree,
  setDataForRenamingChallenge,
} from '../../redux/actions'

const ChallengesPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle('Challenges')

  const eventEntityTypes = useMemo(() => [EVENT_METADATA_TYPES.CHALLENGE], [])

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useChallengesQuery()

  const [saveChallenges, { isLoading: isSaveLoading, error: saveError }] =
    useSaveChallengesMutation(refetchLastEvent)

  const [deleteChallenge] = useDeleteChallengeMutation(refetchLastEvent)

  const { isDirty, data, dataForRenaming } = useSelector(
    state => state.challenges.challenges,
  )

  const hasFullPermission = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.SAVE,
  )

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(
    node => dispatch(addChallenge(node)),
    [dispatch],
  )

  const handleDelete = useCallback(
    ({ id }) => deleteChallenge(id),
    [deleteChallenge],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameChallenge({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id }) => dispatch(setDataForRenamingChallenge({ id })),
    [dispatch],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetChallengeTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveChallenges(data),
    [data, saveChallenges],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Challenges error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  return isFetchLoading || isSaveLoading || isFetchLatestEventLoading ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Challenges</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                renderEvent={renderChallengeEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={!isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      <MetadataTree
        disableLink={!hasPermissionToViewDetails}
        metadataType={METADATA_TYPES.CHALLENGES}
        isDirty={isDirty}
        showSearch
        treeData={treeData}
        editable={hasFullPermission}
        addNode={handleAdd}
        deleteNode={handleDelete}
        renameNode={handleRename}
        setDataForRenaming={handleSetDataForRenaming}
        dataForRenaming={dataForRenaming}
        getLink={({ node: { id } }) =>
          `${APP_ROUTES.MOBILE_APP_ONLY.subMenuItems.CHALLENGES.link}/${id}`
        }
      />
    </>
  )
}

export default ChallengesPage
